import type { SlideFieldsFragment } from "@/generated/graphql";
import { useEditSlideTemplate } from "@/lib/hooks/slides/useEditSlideTemplate";
import { CREATABLE_TEMPLATES, getTemplateLabel, toTemplateEnum } from "@/lib/slides/slideTemplates";
import { convertElementsToPixels } from "@/lib/utils/elementConversion";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { loadSlide } from "@/store/slideEditorSlice";
import type { SlideFields } from "@/types/Slide";
import type { SlideTemplate } from "@ai-teach/types";
import { resolveSlideTemplate } from "@ai-teach/types";
import { useState } from "react";

interface UseEditorActionsModalProps {
  slide: SlideFieldsFragment | undefined;
}

export function useChangeSlideType({ slide: slideParam }: UseEditorActionsModalProps) {
  const dispatch = useAppDispatch();

  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const template = resolveSlideTemplate(slide);

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<SlideTemplate | null>(null);

  const { editSlideTemplate, loading } = useEditSlideTemplate();

  const handleTemplateChange = (newTemplate: SlideTemplate) => {
    if (newTemplate !== template) {
      setPendingTemplate(newTemplate);
      setShowWarningModal(true);
    }
  };

  // Saves directly to the DB; the backend clears elements when the template changes.
  const handleConfirmChange = async () => {
    if (!pendingTemplate || !slideParam?.id) return;

    try {
      const result = await editSlideTemplate(
        slideParam.id,
        slideParam.name,
        toTemplateEnum(pendingTemplate)
      );

      // Reload the whole slide from the mutation result so the editor stays in sync.
      const updatedSlide = Array.isArray(result) ? result[0] : result;
      if (updatedSlide) {
        dispatch(
          loadSlide({
            ...updatedSlide,
            elements: convertElementsToPixels(updatedSlide.elements, pendingTemplate),
          } as SlideFields)
        );
      }

      setShowWarningModal(false);
      setPendingTemplate(null);
    } catch (error) {
      // Already surfaced as a toast by useEditSlideTemplate.
      console.error("Failed to update slide:", error);
    }
  };

  const handleCancelChange = () => {
    setShowWarningModal(false);
    setPendingTemplate(null);
  };

  return {
    template,
    showWarningModal,
    pendingTemplate,
    loading,
    handleTemplateChange,
    handleConfirmChange,
    handleCancelChange,
    CREATABLE_TEMPLATES,
    getTemplateLabel,
  };
}
